import React, { useMemo } from 'react';
import { useRef, useEffect } from 'react';
import ProductCard from './ProductCard';
import { SkeletonCard } from './Skeleton';
import { Link } from 'react-router-dom';

const directions = [1, -1]; // 1 = left, -1 = right

const ProductShowcase = ({ title, products, subtitle, loading, badge, showSeeMore = true, showExclusiveBadge = true, seeMoreLink, rowCount = 2, mobileRows = 3 }) => {
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const row1Position = useRef(0);
  const row2Position = useRef(0);
  const speed = 0.5;

  const singleRow = rowCount === 1;
  const row1Products = useMemo(
    () => (singleRow ? (products || []) : (products || []).filter((_, i) => i % 2 === 0)),
    [products, singleRow]
  );
  const row2Products = useMemo(
    () => (singleRow ? [] : (products || []).filter((_, i) => i % 2 === 1)),
    [products, singleRow]
  );
  const row1Copies = Math.max(8, row1Products.length * 2);
  const row2Copies = Math.max(8, row2Products.length * 2);

  useEffect(() => {
    const track = row1Ref.current;
    if (!track || row1Products.length === 0) return;

    let animationId;
    const step = () => {
      row1Position.current += speed * directions[0];
      const singleWidth = track.scrollWidth / row1Copies;
      if (singleWidth > 0 && row1Position.current >= singleWidth) {
        row1Position.current -= singleWidth;
      }
      track.style.transform = `translateX(-${row1Position.current}px)`;
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);

    const pause = () => cancelAnimationFrame(animationId);
    const resume = () => { animationId = requestAnimationFrame(step); };

    track.parentElement.addEventListener('mouseenter', pause);
    track.parentElement.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animationId);
      track.parentElement.removeEventListener('mouseenter', pause);
      track.parentElement.removeEventListener('mouseleave', resume);
    };
  }, [row1Products, row1Copies]);

  useEffect(() => {
    const track = row2Ref.current;
    if (!track || row2Products.length === 0) return;

    let animationId;
    const step = () => {
      row2Position.current += speed * directions[1];
      const singleWidth = track.scrollWidth / row2Copies;
      if (singleWidth > 0 && row2Position.current < 0) {
        row2Position.current += singleWidth;
      }
      track.style.transform = `translateX(-${row2Position.current}px)`;
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);

    const pause = () => cancelAnimationFrame(animationId);
    const resume = () => { animationId = requestAnimationFrame(step); };

    track.parentElement.addEventListener('mouseenter', pause);
    track.parentElement.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animationId);
      track.parentElement.removeEventListener('mouseenter', pause);
      track.parentElement.removeEventListener('mouseleave', resume);
    };
  }, [row2Products, row2Copies]);

  if (!loading && (!products || products.length === 0)) {
    return null;
  }

  return (
    <section className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">{title}</h2>
            {subtitle && (
              <p className="text-gray-600 mt-2 text-sm">{subtitle}</p>
            )}
            <div className="w-16 h-0.5 bg-gold mx-auto mt-2"></div>
          </div>
          {!loading && showSeeMore && (
            <Link
              to={seeMoreLink || '/shop-all'}
              className="group inline-flex items-center gap-2 px-6 py-2 border border-gold text-gold font-medium rounded-lg hover:bg-gold hover:text-white transition-all duration-300 ease-in-out text-sm"
            >
              See More
              <span className="transform translate-x-0 group-hover:translate-x-1 transition-transform duration-300">
                →
              </span>
            </Link>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {[...Array(mobileRows * 2)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-[3/4] rounded-xl mb-4"></div>
                <div className="bg-gray-200 h-4 rounded w-3/4 mb-2"></div>
                <div className="bg-gray-200 h-4 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* MOBILE STATIC GRID — visible only on mobile */}
            <div className="sm:hidden">
              {loading ? (
                <div className="grid grid-cols-2 gap-4">
                  {[...Array(mobileRows * 2)].map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {products.slice(0, mobileRows * 2).map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>

            {/* DESKTOP MARQUEE — hidden on mobile */}
            <div className="hidden sm:block">
              <div
                className="overflow-hidden w-full"
                style={{
                  position: 'relative',
                  maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                }}
              >
                <div className="flex flex-col gap-4 md:gap-6">
                  {/* ROW 1 — direction: 1 (left) */}
                  {row1Products.length > 0 && (
                    <div className="overflow-hidden w-full">
                      <div
                        ref={row1Ref}
                        className="flex gap-4 md:gap-6 items-stretch"
                        style={{ width: 'max-content', willChange: 'transform' }}
                      >
                        {Array.from({ length: row1Copies }).flatMap((_, copyIndex) =>
                          row1Products.map((product, index) => (
                            <div
                              key={`row1-${copyIndex}-${product.id || index}`}
                              className="flex-shrink-0"
                              style={{ width: '180px' }}
                            >
                              <ProductCard
                                product={product}
                                badge={badge}
                                isNew={product.is_new}
                                isExclusive={showExclusiveBadge ? undefined : false}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* ROW 2 — direction: -1 (right) */}
                  {!singleRow && row2Products.length > 0 && (
                    <div className="overflow-hidden w-full">
                      <div
                        ref={row2Ref}
                        className="flex gap-4 md:gap-6 items-stretch"
                        style={{ width: 'max-content', willChange: 'transform' }}
                      >
                        {Array.from({ length: row2Copies }).flatMap((_, copyIndex) =>
                          row2Products.map((product, index) => (
                            <div
                              key={`row2-${copyIndex}-${product.id || index}`}
                              className="flex-shrink-0"
                              style={{ width: '180px' }}
                            >
                              <ProductCard
                                product={product}
                                badge={badge}
                                isNew={product.is_new}
                                isExclusive={showExclusiveBadge ? undefined : false}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ProductShowcase;
